import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  InputBase,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { CalendarToday } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../../Utils/constants";
import { addServiceRequestVisit } from "../../../../Services/authService";
import '../../../Manage/VendingMachines/VendingMachines.css';
import AddSparePartModal from "./AddSparePartModal";
import DeleteIcon from '@mui/icons-material/Delete';
import SuccessNotification from "../../../../Components/Common/SuccessNotification";
import { StyledTextField } from "../../../../Components/Common/StyledTextField";

const initialVisit = {
  assignedTo: "",
  type: "",
  visitedDate: "",
  status: "",
  notes: "",
  spareParts: []
};

const initialError = {
  assignedTo: "",
  type: "",
  visitedDate: "",
  status: "",
  notes: ""
};

const ServiceDetails = ({ complaintData }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const backgroundCard = mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const btnColor = mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const inputBoxColor = mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const inputBoxColor2 = mode === "dark" ? DARKCOLORS.GreyScale20 : LIGHTCOLORS.GreyScale20;
  const inputBoxColor1 = mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
  const textColor = mode === "dark" ? DARKCOLORS.HeadingLightBackground : LIGHTCOLORS.HeadingLightBackground;
  const labeltextColor = mode === "dark" ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
  const deleteColor = mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;

  const complaintsStatus = [
    { value: "Open", label: "serviceRequest.open" },
    { value: "In progress", label: "serviceRequest.inprogress" },
    { value: "Completed", label: "serviceRequest.completed" }
  ];

  const visitTypes = [
    { value: "Service", label: "serviceRequest.Service" },
    { value: "Maintenance", label: "serviceRequest.Maintenance" }
  ];
  const [visits, setVisits] = useState([]);
  const [errors, setErrors] = useState([]);
  const [openSpareModal, setOpenSpareModal] = useState(false);
  const [currentVisitIdx, setCurrentVisitIdx] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  useEffect(() => {
    if (complaintData?.visits?.length > 0) {
      const formattedVisits = complaintData.visits.map((v) => ({
        assignedTo: v.technician || "",
        type: v.type || "",
        visitedDate: v.visitDate?.slice(0, 10) || "",
        status: v.status || "",
        notes: v.notes || "",
        spareParts: v.spareParts || []
      }));

      setVisits(formattedVisits);
      setErrors(formattedVisits.map(() => ({ ...initialError })));
    } else {
      setVisits([initialVisit]);
      setErrors([initialError]);
    }
  }, [complaintData]);

  const validateVisit = (visit) => {
    let err = {};
    if (!visit.assignedTo) err.assignedTo = t("serviceRequest.assigneeRequired");
    if (!visit.type) err.type = t("serviceRequest.typeRequired");
    if (!visit.visitedDate) err.visitedDate = t("serviceRequest.visitedDateRequired");
    if (!visit.status) err.status = t("serviceRequest.statusRequired");
    if (!visit.notes) err.notes = t("serviceRequest.notesRequired");
    return err;
  };

  const handleVisitChange = (idx, e) => {
    const { name, value } = e.target;

    setVisits((prev) =>
      prev.map((v, i) => (i === idx ? { ...v, [name]: value } : v))
    );

    // Validate on change
    setErrors((prev) =>
      prev.map((err, i) => {
        if (i === idx) {
          const newErr = { ...err };
          if (!value) {
            newErr[name] = `${name[0].toUpperCase() + name.slice(1)} is required`;
          } else {
            newErr[name] = "";
          }
          return newErr;
        }
        return err;
      })
    );
  };

  const handleDeleteVisit = (idx) => {
    if (visits.length === 1) return; // Prevent deleting the last visit

    setVisits((prev) => prev.filter((_, i) => i !== idx));
    setErrors((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddVisit = () => {
    setVisits([...visits, initialVisit]);
    setErrors([...errors, initialError]);
  };

  const handleSave = async () => {
    const idx = visits.length - 1;
    const err = validateVisit(visits[idx]);
    if (Object.keys(err).length > 0) {
      setErrors((prev) =>
        prev.map((e, i) => (i === idx ? { ...e, ...err } : e))
      );
      return;
    }

    try {
      const payload = {
        serviceRequestId: complaintData.id,
        visits: visits.map(v => ({
          technician: v.assignedTo,
          visitDate: `${v.visitedDate}T00:00:00.000Z`,
          notes: v.notes,
          type: v.type,
          status: v.status,
          spareParts: v.spareParts || []
        }))
      };

      const response = await addServiceRequestVisit(payload);

      // Show success message
      setNotification({
        open: true,
        message: t("serviceRequest.servicaSaveMessage"),
        severity: "success"
      });

      // Refresh the data from complaintData (if from API, refetch externally or trigger re-render)
      // Here, we simply reset visits from payload
      const formattedVisits = payload.visits.map(v => ({
        assignedTo: v.technician,
        type: v.type,
        visitedDate: v.visitDate.slice(0, 10),
        status: v.status,
        notes: v.notes,
        spareParts: v.spareParts
      }));
      setVisits(formattedVisits);
      setErrors(formattedVisits.map(() => ({ ...initialError })));
    } catch (error) {
      console.error("Failed to save visit", error);
      setNotification({
        open: true,
        message: t("serviceRequest.servicaErrorMessage"),
        severity: "error"
      });
    }
  };

  const handleAddSparePart = (part) => {
    setVisits((prev) =>
      prev.map((visit, i) =>
        i === currentVisitIdx
          ? { ...visit, spareParts: [...(visit.spareParts || []), part] }
          : visit
      )
    );
    setOpenSpareModal(false);
  };

  const handleDeleteSparePart = (visitIdx, partIdx) => {
    setVisits((prev) =>
      prev.map((visit, i) =>
        i === visitIdx
          ? {
            ...visit,
            spareParts: visit.spareParts.filter((_, idx) => idx !== partIdx)
          }
          : visit
      )
    );
  };

  const CustomDeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="15" viewBox="0 0 12 14" fill="none">
      <path d="M11.5 3.48991C9.5575 3.29741 7.60333 3.19824 5.655 3.19824C4.5 3.19824 3.345 3.25658 2.19 3.37324L1 3.48991M4.20833 2.90047L4.33667 2.1363C4.43 1.58214 4.5 1.16797 5.48583 1.16797H7.01417C8 1.16797 8.07583 1.60547 8.16333 2.14214L8.29167 2.90047M10.2458 5.33276L9.86661 11.2069C9.80244 12.1228 9.74994 12.8344 8.12244 12.8344H4.37744C2.74994 12.8344 2.69744 12.1228 2.63328 11.2069L2.25411 5.33276M5.27588 9.6263H7.21838M4.79167 7.29297H7.70833" stroke={deleteColor} stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );

  return (
    <Box sx={{ mt: 7 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <h2 className="machine-name-text" style={{ padding: "0px" }}>{t("serviceRequest.serviceDetails")}</h2>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ backgroundColor: btnColor, textTransform: "none", borderRadius: 20 }}
        >
          {t("serviceRequest.save")}
        </Button>
      </Box>

      {visits.map((visit, idx) => (
        <Box
          key={idx}
          className="complaint-form-container"
          sx={{ backgroundColor: inputBoxColor, borderRadius: 2, p: 3, mb: 3 }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: 'center' }}>
            <h2 className="machine-name-text">
              {`${t("serviceRequest.visit")} ${idx + 1}`}
            </h2>
            {visits.length > 1 && (
              <Button
                size="small"
                onClick={() => handleDeleteVisit(idx)}
                sx={{
                  color: deleteColor,
                  border: `1px solid ${deleteColor}`,
                  borderRadius: "20px",
                  textTransform: "none",
                  minWidth: "auto",
                  fontSize: 13,
                  px: 2
                }}
              >
                {t("membershipCards.delete") || "Delete"}
              </Button>
            )}
          </Box>

          <Grid container spacing={2}>
            {/* Left Column (Assigned To, Type, Visited Date, Status) */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid  item xs={12} md={6}>
                  <Box className="service-grid">

                  <StyledTextField
                    label={t("serviceRequest.assignedTo")}
                    name="assignedTo"
                    value={visit.assignedTo}
                    onChange={(e) => handleVisitChange(idx, e)}
                    inputBoxColor={backgroundCard}
                    error={errors[idx]?.assignedTo}
                    required
                  />
                  </Box>

                </Grid>

                <Grid item xs={12} md={6}>
                  <Box className="service-grid">

                  <StyledTextField
                    label={t("serviceRequest.type")}
                    name="type"
                    value={visit.type}
                    onChange={(e) => handleVisitChange(idx, e)}
                    inputBoxColor={backgroundCard}
                    error={errors[idx]?.type}
                    required
                  >
                    {visitTypes.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box className="service-grid">

                  <StyledTextField
                    isDate
                    label={t("serviceRequest.visitedDate")}
                    name="visitedDate"
                    value={visit.visitedDate}
                    onChange={(e) => handleVisitChange(idx, e)}
                    inputBoxColor={backgroundCard}
                    error={errors[idx]?.visitedDate}
                    required
                    inputProps={{ min: new Date().toISOString().split("T")[0] }}
                  />
                </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box className="service-grid">
                  <StyledTextField
                    label={t("serviceRequest.status")}
                    name="status"
                    value={visit.status}
                    onChange={(e) => handleVisitChange(idx, e)}
                    inputBoxColor={backgroundCard}
                    error={errors[idx]?.status}
                    required
                  >
                    {complaintsStatus.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column (Technician Notes) */}
            <Grid item xs={12} md={6}>
              <Box className="service-grid">
              <StyledTextField
                label={t("serviceRequest.technicianNotes")}
                name="notes"
                value={visit.notes}
                onChange={(e) => handleVisitChange(idx, e)}
                inputBoxColor={backgroundCard}
                error={errors[idx]?.notes}
                multiline
                minRows={4}
                required
              />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography className="machine-name-text">
                {t("serviceRequest.sparePartsUsed")}
              </Typography>

              <button
                variant="outlined"
                size="small"
                className="view-more-btn"
                onClick={() => {
                  setCurrentVisitIdx(idx);
                  setOpenSpareModal(true);
                }}
                style={{ color: btnColor, background: 'transparent' }}
              >
                {t("serviceRequest.addPart")}
              </button>
            </Box>
            <TableContainer sx={{ overflowX: 'auto', mb: 2,  }}>
              <Table sx={{
                borderCollapse: "collapse",
                width: "100%",
                "& .MuiTableCell-root": {
                  border: "none",
                  padding: "8px 12px",
                  // minWidth: { xs: 400, md: "auto" },
                  whiteSpace: { xs: 'nowrap', md: 'normal' },
                },
              }}>
                <TableHead>
                  <TableRow sx={{
                    borderTop: `1px solid ${inputBoxColor1}`,
                    borderBottom: `1px solid ${inputBoxColor1}`,
                  }}>
                    <TableCell sx={{ color: textColor, fontWeight: 600 }}>{t("serviceRequest.SNo")}</TableCell>
                    <TableCell sx={{ color: textColor, fontWeight: 600 }}>{t("serviceRequest.name")}</TableCell>
                    <TableCell sx={{ color: textColor, fontWeight: 600 }}>{t("serviceRequest.serialNo")}</TableCell>
                    <TableCell sx={{ color: textColor, fontWeight: 600 }}>{t("serviceRequest.quantity")}</TableCell>
                    <TableCell sx={{ color: textColor, fontWeight: 600 }}>{t("serviceRequest.price")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visit.spareParts?.length > 0 ? (
                    visit.spareParts.map((part, pidx) => (
                      <TableRow key={pidx} sx={{
                        "&:nth-of-type(even)": { backgroundColor: backgroundCard },
                      }}>
                        <TableCell sx={{ color: textColor }}>{pidx + 1}</TableCell>
                        <TableCell sx={{ color: textColor }}>{part.name}</TableCell>
                        <TableCell sx={{ color: textColor }}>{part.serialNo}</TableCell>
                        <TableCell sx={{ color: textColor }}>{part.quantity}</TableCell>
                        <TableCell sx={{ color: textColor }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            € {part.price}
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteSparePart(idx, pidx)}
                              sx={{
                                padding: "6px",
                                backgroundColor: inputBoxColor2,
                                borderRadius: "50%",
                              }}
                            >
                              <CustomDeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: "#718096", textAlign: "center", py: 2, fontSize: 14 }}>
                        {t('serviceRequest.noSparePartsUsed')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      ))
      }

      <Box sx={{ textAlign: "center", color: btnColor, mt: 2, cursor: 'pointer' }} className='sort-filter-text' onClick={handleAddVisit} >
        + {t("serviceRequest.addAnotherVisit")}
      </Box>
      <AddSparePartModal
        open={openSpareModal}
        onClose={() => setOpenSpareModal(false)}
        onSave={handleAddSparePart}
      />
      <SuccessNotification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification({ ...notification, open: false })}
      />

      {/* <Typography className="addmachine-text-heading" sx={{ color: textColor, fontSize: 12, mt: 5 }}>
        {t("serviceRequest.previousServiceHistory")}
      </Typography> */}
    </Box >
  );
};

export default ServiceDetails;
